/**
 * Hydratation progressive des modules data-module.
 *
 * - Chargement immédiat par défaut
 * - data-context="@visible" : lazy (idle + IntersectionObserver partagé)
 * - modules avec { pause, resume, destroy } : pause hors viewport (IO lifecycle)
 * - data-module-media : chargement conditionnel avec retry au resize
 */

const hydratedElements = new WeakMap();
const moduleCache = new Map();
const pendingLoads = new Map();
const visibilityWaiters = new Map();
const mediaWatchers = new WeakMap();
/** @type {Map<Element, IntersectionObserver>} */
const lifecycleObservers = new Map();

const defaultObserverOptions = {
    root: null,
    rootMargin: "50px",
    threshold: 0.1,
};

let visibilityObserver = null;

const getVisibilityObserver = () => {
    if (visibilityObserver) {
        return visibilityObserver;
    }

    visibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (!entry.isIntersecting) {
                continue;
            }

            const finish = visibilityWaiters.get(entry.target);

            if (!finish) {
                continue;
            }

            finish();
        }
    }, defaultObserverOptions);

    return visibilityObserver;
};

const shouldWaitForVisibility = (element) => {
    const context = element.dataset.context;

    if (context && context.includes("@visible")) {
        return true;
    }

    if (element.dataset.moduleVisible !== undefined) {
        return element.dataset.moduleVisible !== "false";
    }

    return false;
};

const watchMediaCondition = (element) => {
    const media = element.dataset.moduleMedia;

    if (!media) {
        return true;
    }

    const mql = window.matchMedia(media);

    if (mql.matches) {
        return true;
    }

    if (mediaWatchers.has(element)) {
        return false;
    }

    const onChange = () => {
        if (!mql.matches) {
            return;
        }

        mql.removeEventListener("change", onChange);
        mediaWatchers.delete(element);
        hydrateElement(element);
    };

    mediaWatchers.set(element, { mql, onChange });
    mql.addEventListener("change", onChange);

    return false;
};

const waitForVisibility = (element) => {
    return new Promise((resolve) => {
        const observer = getVisibilityObserver();
        let settled = false;

        const finish = () => {
            if (settled) {
                return;
            }

            settled = true;
            visibilityWaiters.delete(element);
            observer.unobserve(element);
            resolve(true);
        };

        visibilityWaiters.set(element, finish);
        observer.observe(element);
    });
};

/**
 * @example data-module="common/footer" → assets/common/footer/footer.js
 * @example data-module="heros/hero-homepage" → assets/heros/hero-homepage/hero-homepage.js
 * @example data-module="strates/strate-showcase" → assets/strates/strate-showcase/strate-showcase.js
 */
export const loadModule = (moduleName) => {
    if (moduleCache.has(moduleName)) {
        return Promise.resolve(moduleCache.get(moduleName));
    }

    if (pendingLoads.has(moduleName)) {
        return pendingLoads.get(moduleName);
    }

    const file = moduleName.split("/").pop();

    const promise = import(
        /* webpackChunkName: "[request]" */
        /* webpackInclude: /\/(common|components|heros|strates)\/[^/]+\/[^/]+\.js$/ */
        `../../${moduleName}/${file}.js`
    )
        .then((module) => {
            const constructor = module.default;
            moduleCache.set(moduleName, constructor);
            return constructor;
        })
        .catch((error) => {
            console.error(`[Hydrate] Failed to load module: ${moduleName}`, error);
            return null;
        })
        .finally(() => {
            pendingLoads.delete(moduleName);
        });

    pendingLoads.set(moduleName, promise);

    return promise;
};

export const prefetchModule = (moduleName) => {
    if (!moduleName || moduleCache.has(moduleName) || pendingLoads.has(moduleName)) {
        return;
    }

    loadModule(moduleName);
};

const normalizeLifecycle = (instance) => {
    if (typeof instance === "function") {
        return { destroy: instance, pause: null, resume: null };
    }

    if (!instance || typeof instance !== "object") {
        return { destroy: null, pause: null, resume: null };
    }

    return {
        destroy: typeof instance.destroy === "function" ? instance.destroy : null,
        pause: typeof instance.pause === "function" ? instance.pause : null,
        resume: typeof instance.resume === "function" ? instance.resume : null,
    };
};

const stopLifecycleObserver = (element) => {
    const observer = lifecycleObservers.get(element);

    if (!observer) return;

    observer.disconnect();
    lifecycleObservers.delete(element);
};

const startLifecycleObserver = (element, lifecycle) => {
    const { pause, resume } = lifecycle;

    if (!pause || !resume) return;

    stopLifecycleObserver(element);

    let isPaused = false;

    const observer = new IntersectionObserver(([entry]) => {
        if (!entry) return;

        if (entry.isIntersecting) {
            if (!isPaused) return;

            isPaused = false;
            resume();
            return;
        }

        if (isPaused) return;

        isPaused = true;
        pause();
    }, {
        root: null,
        rootMargin: "150px 0px",
        threshold: 0,
    });

    observer.observe(element);
    lifecycleObservers.set(element, observer);
};

const initModule = (element, ModuleConstructor) => {
    if (!ModuleConstructor) {
        hydratedElements.set(element, { status: "error" });
        return;
    }

    try {
        const instance = ModuleConstructor(element);
        const lifecycle = normalizeLifecycle(instance);

        hydratedElements.set(element, {
            status: "loaded",
            ...lifecycle,
        });

        element.dataset.hydrated = "true";
        startLifecycleObserver(element, lifecycle);
    } catch (error) {
        console.error(`[Hydrate] Error initializing module: ${element.dataset.module}`, error);
        hydratedElements.set(element, { status: "error", error });
    }
};

const runHydration = (element, moduleName) => {
    const start = () => loadModule(moduleName).then((ModuleConstructor) => initModule(element, ModuleConstructor));

    if (shouldWaitForVisibility(element)) {
        return waitForVisibility(element).then(start);
    }

    return start();
};

export const hydrateElement = (element) => {
    const existing = hydratedElements.get(element);

    if (existing && existing.status !== "error") {
        return;
    }

    const moduleName = element.dataset.module;

    if (!moduleName) {
        return;
    }

    if (!watchMediaCondition(element)) {
        return;
    }

    hydratedElements.set(element, { status: "pending" });

    runHydration(element, moduleName);
};

export const hydrate = (container = document.documentElement) => {
    const elements = container.querySelectorAll("[data-module]");
    const immediate = [];
    const lazy = [];

    elements.forEach((element) => {
        if (shouldWaitForVisibility(element)) {
            lazy.push(element);
        } else {
            immediate.push(element);
        }
    });

    immediate.forEach(hydrateElement);

    if ("requestIdleCallback" in window) {
        lazy.forEach((element) => {
            requestIdleCallback(() => hydrateElement(element), { timeout: 2000 });
        });
    } else {
        lazy.forEach((element, index) => {
            setTimeout(() => hydrateElement(element), index * 10);
        });
    }
};

export const prefetchFromAnchors = (root = document) => {
    const links = root.querySelectorAll('a[href^="#"]');

    links.forEach((link) => {
        link.addEventListener(
            "mouseenter",
            () => {
                const id = (link.getAttribute("href") || "").slice(1);

                if (!id) {
                    return;
                }

                const target = document.getElementById(id);

                if (!target) {
                    return;
                }

                const section = target.closest("[data-module]") ?? target;
                const moduleName = section?.dataset?.module;

                if (moduleName) {
                    prefetchModule(moduleName);
                    hydrateElement(section);
                }
            },
            { passive: true },
        );
    });
};

export const destroy = (element) => {
    const data = hydratedElements.get(element);

    stopLifecycleObserver(element);

    if (data?.destroy) {
        try {
            data.destroy();
        } catch (error) {
            console.error("[Hydrate] Error destroying module", error);
        }
    }

    const watcher = mediaWatchers.get(element);

    if (watcher) {
        watcher.mql.removeEventListener("change", watcher.onChange);
        mediaWatchers.delete(element);
    }

    hydratedElements.delete(element);
    delete element.dataset.hydrated;
};

export const destroyAll = (container = document.documentElement) => {
    const elements = container.querySelectorAll("[data-module][data-hydrated]");
    elements.forEach(destroy);
};

export const observe = (container = document.body) => {
    const mutationObserver = new MutationObserver((mutations) => {
        for (let i = 0; i < mutations.length; i++) {
            const mutation = mutations[i];

            for (let j = 0; j < mutation.addedNodes.length; j++) {
                const node = mutation.addedNodes[j];

                if (node.nodeType !== Node.ELEMENT_NODE) {
                    continue;
                }

                if (node.dataset?.module) {
                    hydrateElement(node);
                }

                const descendants = node.querySelectorAll?.("[data-module]");

                if (descendants?.length) {
                    descendants.forEach(hydrateElement);
                }
            }
        }
    });

    mutationObserver.observe(container, {
        childList: true,
        subtree: true,
    });

    return () => mutationObserver.disconnect();
};

export default {
    hydrate,
    hydrateElement,
    loadModule,
    prefetchModule,
    prefetchFromAnchors,
    destroy,
    destroyAll,
    observe,
};
